import { encryptForLogin } from '../utils/crypto'
import {
  getAccessToken,
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
  setUsername,
} from '../utils/session'
import {
  GET_CONFIG_API,
  GET_TOKEN_API,
  AUTHENTICATION_API,
  BASE_URL,
  TERMS_CONDITIONS_API,
  ACCEPT_TERMS_CONDITIONS_API,
  UPDATE_PASSWORD_API,
  GENERATE_OTP_FOR_MFA_API,
  VERIFY_OTP_FOR_MFA_API,
  SET_MFA_CONFIG_API,
  REFRESH_TOKEN_API,
  LOGOUT_API,
  CLIENT_ID_API,
  GET_CONFIG_SSO,
  GET_EMPLOYEE_LIST,
  LOGIN_SSO_API,
  GET_USER_ROLE_API
} from '../endpoints/apiEndpoints'

import { sanitizeForLog, sanitizeText } from "../constants"

/** 1) MFA Config */
export async function fetchMfaConfig({ username, password,userAlreadyLogin }) {
  const encryptedUsername = await encryptForLogin(username);
  const encryptedPassword = await encryptForLogin(password);

  const res = await fetch(`${BASE_URL}/${GET_CONFIG_API}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      username: encryptedUsername,
      password: encryptedPassword,
      ...(userAlreadyLogin ? { userAlreadyLogin: true } : {}),

    }),
  });

  if (!res.ok) {
    throw new Error(`MFA config request failed: ${res.status}`);
  }

  const json = await res.json();

  // Validate response
  if (!json?.success) {
    const errorCode = json?.errors?.[0]?.errorCode;
    switch (errorCode) {
      case 'USER_INACTIVE':
        throw new Error('User is inactive, contact admin for support.');
      case 'USER_LOCKED':
        throw new Error('User is locked, contact admin for support.');
      case 'ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED':
      case 'ALREADY_LOGGED_IN':
        throw new Error('User is already logged in.');
      case 'EXPIRE_PASSWORD_ERROR':
        throw new Error('Password expired. Please reset your password.');
      default:
        throw new Error('Incorrect username or password.');
    }
  }

  return json;
}



/** 2) Get Token */
export async function getToken({ username, password, secretLink, otp, userAlreadyLogin }) {

  const encryptedUsername = await encryptForLogin(username);
  const encryptedPassword = await encryptForLogin(password);
  const encryptedOtp = await encryptForLogin(otp);
  const res = await fetch(`${BASE_URL}/${GET_TOKEN_API}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      preferred_username: encryptedUsername,
      password: encryptedPassword,
      ...(secretLink ? { secretLink } : {}),
      ...(otp ? { otp: encryptedOtp } : {}),
      ...(userAlreadyLogin ? { userAlreadyLogin: true } : {}),
    }),
  });

  if (!res.ok) {
    let errorMsg = 'Login failed';
    try {
      const t = await res.text();
      const error = JSON.parse(t);
      errorMsg = error?.errors?.pop()?.message || errorMsg;
    } catch { /* ignore */ }
    throw new Error(sanitizeForLog(errorMsg));
  }

  const json = await res.json();
  if (!json?.success || !json?.data?.access_token) {
    if (json?.errors[0]?.errorCode === "USER_INACTIVE") {
      throw new Error('User is inactive, contact admin for support')
    } else if (json?.errors[0]?.errorCode === "USER_LOCKED") {
      throw new Error('User is locked, contact admin for support')
    } else if (json?.errors[0]?.errorCode === "ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED" || json?.errors[0]?.errorCode === "ALREADY_LOGGED_IN") {
      throw new Error(json?.errors[0]?.errorCode)
    } else if (json?.errors[0]?.errorCode === "EXPIRE_PASSWORD_ERROR") {
      throw new Error('Password expired. Please reset your password.');
    }
    throw new Error('Entered OTP does not match')
  }

  return json.data;
}

/* ------------------ 3) Authenticate ------------------ */
export async function authenticate() {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${AUTHENTICATION_API}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Authenticate failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success || !json?.data)
    throw new Error('Authenticate response missing data');

  return json.data;
}

/* ------------------ 4) loginAndAuthenticate ------------------ */
export async function loginAndAuthenticate({
  username,
  password,
  secretLink,
  otp,
  userAlreadyLogin
}) {

  const tokenData = await getToken({ username, password, secretLink, otp, userAlreadyLogin })
  setAccessToken(tokenData.access_token, Number(tokenData.expires_in))
  setRefreshToken(tokenData.refresh_token)
  setUsername(username) // Store username in session
  const authData = await authenticate()
  return { tokenData, authData }
}

/* ------------------ 5) Terms and Conditions ------------------ */
export async function fetchTermsAndConditions() {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${TERMS_CONDITIONS_API}`, {
    method: 'GET',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Terms fetch failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success) throw new Error('Terms response indicates failure');
  return json.data;
}

export async function acceptTermsAndConditions() {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${ACCEPT_TERMS_CONDITIONS_API}`, {
    method: 'PUT',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Accept terms failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error('Accept terms and conditions response indicates failure');

  return json.data;
}

/* ------------------ 6) Update Password ------------------ */
export async function updatePassword({ oldPassword, password, username }) {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const encryptedUsername = await encryptForLogin(username);
  const encryptedPassword = await encryptForLogin(password);
  const encryptedOldPassword = await encryptForLogin(oldPassword);

  const res = await fetch(`${BASE_URL}/${UPDATE_PASSWORD_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      oldPassword: encryptedOldPassword,
      password: encryptedPassword,
      username: encryptedUsername,
    }),
  });

  const json = await res.json();
  if (!json?.success) throw new Error(sanitizeForLog(json.errors?.[0]?.message || 'Error'));
  return json.data;
}

/* ------------------ 7) Generate OTP for MFA ------------------ */
export async function generateOtpForMFA({
  secretLink = '',
  username,
  mfa,
  isForogtScreen = false,
}) {
  let token;
  if (!isForogtScreen) {
    token = getAccessToken();
    if (!token) throw new Error('Missing access token in sessionStorage');
  }

  const encryptedUsername = await encryptForLogin(username);

  const res = await fetch(`${BASE_URL}/${GENERATE_OTP_FOR_MFA_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      secretLink,
      username: encryptedUsername,
      ...(mfa && { mfa: mfa }),
    }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Generate OTP failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error(sanitizeForLog(json.errors?.pop()?.message || 'Error'));

  return json.data;
}

/* ------------------ 8) Verify OTP for MFA ------------------ */
export async function verifyOtpForMFA({
  secretLink,
  username,
  mfa,
  otp,
  isForogtScreen = false,
}) {
  let token;
  if (!isForogtScreen) {
    token = getAccessToken();
    if (!token) throw new Error('Missing access token in sessionStorage');
  }
  const encryptedUsername = await encryptForLogin(username);
  const encryptedOtp = await encryptForLogin(otp);
  const res = await fetch(`${BASE_URL}/${VERIFY_OTP_FOR_MFA_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      secretLink,
      username: encryptedUsername,
      ...(mfa && { mfa: mfa }),
      otp: encryptedOtp,
    }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Verify OTP failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error(sanitizeForLog(json.errors?.pop()?.message || 'Error'));

  return json.data;
}

/** 9) Set MFA Config */
export async function setMfaConfig({
  secretLink,
  otpMFA = true,
  otp,
  isMFASet = false,
}) {
  const token = getAccessToken()
  if (!token) throw new Error('Missing access token in sessionStorage')

  const encryptedOtp = await encryptForLogin(otp);

  const res = await fetch(`${BASE_URL}/${SET_MFA_CONFIG_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      secretLink,
      otpMFA,
      otp: encryptedOtp,
      isMFASet,
    }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Set MFA config failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error('Set MFA config response indicates failure');

  return json.data;
}

/* ------------------ 10) Get MFA Config Post ------------------ */
export async function getMfaConfigPost({ username, password }) {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${GET_CONFIG_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Get MFA config failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error('Get MFA config response indicates failure');

  return json.data;
}

/* ------------------ 11) Get Token with MFA ------------------ */
export async function getTokenWithMFA({
  preferred_username,
  password,
  secretLink,
  otp,
}) {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${GET_TOKEN_API}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      preferred_username,
      password,
      secretLink,
      otp,
    }),
  });

  if (!res.ok) {
    let errorMsg = 'Get token with MFA failed';
    try {
      const t = await res.text();
      const error = JSON.parse(t);
      errorMsg = error?.errors?.pop()?.message || errorMsg;
    } catch { }
    throw new Error(sanitizeForLog(errorMsg));
  }

  const json = await res.json();
  if (!json?.success || !json?.data?.access_token)
    throw new Error('Token response missing access_token');

  setAccessToken(json.data.access_token, Number(json.data.expires_in));
  if (json.data.refresh_token)
    setRefreshToken(json.data.refresh_token);

  return json.data;
}

/* ------------------ 12) Refresh Token ------------------ */
export async function refreshToken() {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue)
    throw new Error('Missing refresh token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${REFRESH_TOKEN_API}`, {
    method: 'GET',
    headers: { Authorization: refreshTokenValue },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Refresh token failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success || !json?.data?.access_token)
    throw new Error('Refresh token response missing access_token');

  setAccessToken(json.data.access_token, Number(json.data.expires_in));
  if (json.data.refresh_token)
    setRefreshToken(json.data.refresh_token);

  return json.data;
}

/* ------------------ 13) Logout ------------------ */
export async function logoutUser() {
  const token = getRefreshToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const res = await fetch(`${BASE_URL}/${LOGOUT_API}`, {
    method: 'GET',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Logout failed: ${res.status} ${t}`));
  }

  const json = await res.json().catch(() => null);
  if (!json?.success)
    throw new Error('Logout response indicates failure');

  return json.data;
}

/* ------------------ 14) SSO ------------------ */
export async function getClientId() {
  const res = await fetch(sanitizeText(`${BASE_URL}/${CLIENT_ID_API}`), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Client ID fetch failed: ${res.status} ${t}`));
  }

  const json = await res.json();
  if (!json?.success)
    throw new Error('Client ID response indicates failure');

  return json;
}

export async function getSSOConfig(params) {
  let url = `${BASE_URL}/${GET_CONFIG_SSO}`;
  if (params) url += `?clientName=${encodeURIComponent(params)}`;

  const res = await fetch(sanitizeText(url), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(sanitizeForLog(`Client config fetch failed: ${res.status} ${t}`));
  }

  const json = await res.json();

  // Validate response
  if (!json?.success) {
    const errorCode = json?.errors?.[0]?.errorCode;

    switch (errorCode) {
      case 'USER_INACTIVE':
        throw new Error('User is inactive, contact admin for support.');
      case 'USER_LOCKED':
        throw new Error('User is locked, contact admin for support.');
      case 'ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED':
      case 'ALREADY_LOGGED_IN':
        throw new Error('User is already logged in.');
      default:
        throw new Error('Incorrect username or password.');
    }
  }

  return json;
}

export async function getEmployeeList({ sessionId }) {
  const res = await fetch(`${BASE_URL}/${GET_EMPLOYEE_LIST}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ sessionId }),
  });

  if (!res.ok) throw new Error(`Employee list failed: ${res.status}`);
  return res.json();
}

export async function loginSSO({ sessionId, userId }) {
  const res = await fetch(`${BASE_URL}/${LOGIN_SSO_API}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ sessionId, id: userId }),
  });

  if (!res.ok) throw new Error(`Login SSO failed: ${res.status}`);
  return res.json();
}

export async function getSSOToken({ sessionId, userAlreadyLogin }) {
  const res = await fetch(`${BASE_URL}/${GET_TOKEN_API}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({
      sessionId,
      ...(userAlreadyLogin ? { userAlreadyLogin: true } : {}),
    }),
  })
  if (!res.ok) {
    let errorMsg = 'Get SSO token failed';
    try {
      const t = await res.text();
      const error = JSON.parse(t);
      errorMsg = error?.errors?.pop()?.message || errorMsg;
    } catch { }
    throw new Error(sanitizeForLog(errorMsg));
  }
  const json = await res.json()
  if (!json?.success || !json?.data?.access_token) {
    if (json?.errors[0]?.errorCode === "USER_INACTIVE") {
      throw new Error('User is inactive, contact admin for support')
    } else if (json?.errors[0]?.errorCode === "USER_LOCKED") {
      throw new Error('User is locked, contact admin for support')
    } else if (json?.errors[0]?.errorCode === "ALREADY_LOGGED_IN_MULTI_SESSION_NOT_ALLOWED" || json?.errors[0]?.errorCode === "ALREADY_LOGGED_IN") {
      throw new Error(json?.errors[0]?.errorCode)
    }
    throw new Error('Token response missing access_token')
  }
  const tokenData = json.data
  // set tokens & username here
  setAccessToken(tokenData.access_token, Number(tokenData.expires_in))
  setRefreshToken(tokenData.refresh_token)
  return tokenData
}

export async function getUserRole() {
  const token = getAccessToken();
  if (!token) throw new Error('Missing access token in sessionStorage');

  const url = `${BASE_URL}/${GET_USER_ROLE_API}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
  });

  // Always read body once
  const bodyText = await res.text();

  if (!res.ok) {
    throw new Error(`Get user role failed: ${res.status} ${bodyText}`);
  }

  // Try parsing as JSON, fallback to raw text
  let parsed;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    parsed = bodyText;
  }

  // Handle possible shapes
  if (typeof parsed === 'object' && parsed !== null) {
    if (parsed.success === false) {
      throw new Error('User role response indicates failure');
    }
    return parsed.data ?? parsed.role ?? parsed;
  }

  // If plain string (e.g. "ADMIN")
  return parsed.trim();
}

